import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ArrowsClockwise, Broadcast, Lightning, Pause, Play, WifiHigh, Warning, SignOut,
  CornersOut, CornersIn, FileCsv, Broadcast as BroadcastIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { fmtTimeET } from "@/lib/etTime";

/**
 * Event Command Post — dark-mode operational dashboard. Design mirrors
 * forms.osmcops.com/command-post:
 *
 *   • Deep-navy full-bleed background, no BSVAC chrome
 *   • Top status bar (Dashboard back link · Real-Time · LIVE · Pause · Refresh)
 *   • Big "Event Command Post" title w/ yellow lightning bolt + connection
 *     line + event picker on the right
 *   • Four KPI cards (Total Patients / Transported / RMA / In Triage)
 *   • Triage Distribution row — five colour-blocked tiles + stacked % bar
 *   • Three secondary cards (Open Forms / Closed Forms / Triage Centres)
 *   • A patient list section below (open forms) or an empty-state banner
 *
 * Data comes from `GET /api/triage/command-post`, polled every 30 s. The
 * user can Pause / Resume polling. When paused we still show the last
 * good snapshot.
 */
const REFRESH_MS = 30 * 1000;

const TAG_META = {
  green:  { label: "Green",  sub: "Minor",     bg: "bg-emerald-500", text: "text-emerald-500", bar: "#10B981" },
  yellow: { label: "Yellow", sub: "Delayed",   bg: "bg-yellow-400",  text: "text-yellow-400",  bar: "#F5C400" },
  orange: { label: "Orange", sub: "Urgent",    bg: "bg-orange-500",  text: "text-orange-500",  bar: "#F97316" },
  red:    { label: "Red",    sub: "Immediate", bg: "bg-red-600",     text: "text-red-500",     bar: "#DC2626" },
  black:  { label: "Black",  sub: "Expectant", bg: "bg-slate-900",   text: "text-slate-100",   bar: "#0F172A" },
};
const TAG_ORDER = ["green", "yellow", "orange", "red", "black"];

const LOGO_STANDBY = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/rcm1qmbk_BSVAC%20Patch.png";

function fmtClock(iso) {
  // ET, 24-hour military time — matches the paperwork downstream.
  return fmtTimeET(iso);
}

export default function TriageCommandPost() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState({ buckets: {}, total: 0, generated_at: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [paused, setPaused] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(""); // "" = All events
  const [eventOptions, setEventOptions] = useState([]);       // first-class events
  const [tvMode, setTvMode] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const wsRef = useRef(null);
  const rootRef = useRef(null);

  const authorized = isSupervisorOrAbove(user);
  const canExportCsv = isSupervisorOrAbove(user);

  // Fetch active events so the dropdown lists real events, not free-text.
  useEffect(() => {
    if (!authorized) return;
    api.get("/triage/events").then(({ data }) => setEventOptions(data?.events || [])).catch(() => setEventOptions([]));
  }, [authorized]);

  // --- data loader (used by polling + manual + WS refresh) ------------------
  const loadRef = useRef(null);
  useEffect(() => {
    if (!authorized) return undefined;
    let cancelled = false;
    async function load() {
      setRefreshing(true);
      try {
        const params = selectedEventId ? { event_id: selectedEventId } : {};
        const { data } = await api.get("/triage/command-post", { params });
        if (!cancelled) { setData(data); setErr(""); }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Could not load command post");
      } finally {
        if (!cancelled) { setLoading(false); setRefreshing(false); }
      }
    }
    loadRef.current = load;
    load();
    if (paused) return () => { cancelled = true; };
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [authorized, paused, selectedEventId]);

  // --- WebSocket subscription — instant updates on new / changed patients ---
  useEffect(() => {
    if (!authorized) return undefined;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    // The notifications WS is at /api/notifications/ws and pulls the JWT from
    // the httpOnly cookie already set by the login flow.
    const wsUrl = backend.replace(/^http/, "ws") + "/api/notifications/ws";
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return undefined; }
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg?.event === "triage.patient_created" || msg?.event === "triage.patient_updated") {
          // Simply refetch — cheaper than trying to merge and gets us the
          // correct bucket assignments + KPI totals guaranteed.
          if (!paused && loadRef.current) loadRef.current();
          // Immediate-patient alert: red-tag on a NEW record → flash banner + chime.
          if (msg.event === "triage.patient_created" && (msg.data?.triage_tag_color || "").toLowerCase() === "red") {
            triggerImmediateAlert(msg.data);
          }
        }
      } catch { /* ignore non-JSON keepalives */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [authorized, paused]);

  // --- Immediate-patient chime + red banner ---------------------------------
  const [immediateAlert, setImmediateAlert] = useState(null); // { first_name, last_name, id } | null
  const alertTimerRef = useRef(null);
  function triggerImmediateAlert(patient) {
    setImmediateAlert(patient || {});
    // Auto-dismiss after 15s unless the operator clicks the banner
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    alertTimerRef.current = setTimeout(() => setImmediateAlert(null), 15000);
    // Soft chime via WebAudio — no asset needed, two-tone ascending beep.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const audio = new Ctx();
      const play = (freq, when, dur) => {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, audio.currentTime + when);
        gain.gain.exponentialRampToValueAtTime(0.28, audio.currentTime + when + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + when + dur);
        osc.connect(gain).connect(audio.destination);
        osc.start(audio.currentTime + when);
        osc.stop(audio.currentTime + when + dur + 0.02);
      };
      play(880, 0.00, 0.28);
      play(1320, 0.32, 0.34);
    } catch { /* browser might block without a user gesture — banner still shows */ }
  }

  // --- TV Mode --------------------------------------------------------------
  async function toggleTv() {
    const next = !tvMode;
    setTvMode(next);
    try {
      if (next && rootRef.current && rootRef.current.requestFullscreen) {
        await rootRef.current.requestFullscreen();
      } else if (!next && document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch { /* browser may block without user gesture — visual mode still applies */ }
  }
  // Exit TV mode when the user hits the browser Esc key
  useEffect(() => {
    function onFs() { if (!document.fullscreenElement) setTvMode((v) => v && false); }
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // --- CSV export -----------------------------------------------------------
  async function downloadCsv() {
    setCsvBusy(true);
    try {
      const params = selectedEventId ? { event_id: selectedEventId } : {};
      const resp = await api.get("/triage/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const ev = eventOptions.find((e) => e.id === selectedEventId);
      a.download = `triage_${(ev?.name || "all_events").replace(/\W+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV export failed");
    } finally { setCsvBusy(false); }
  }

  // Flatten every bucket so we can compute KPIs regardless of tag colour.
  const allRecords = useMemo(() => {
    const out = [];
    Object.values(data.buckets || {}).forEach((arr) => { if (Array.isArray(arr)) out.push(...arr); });
    return out;
  }, [data]);

  // Records shown — server already applies event filter but keep client
  // filter for legacy records that only have `event_name` (no event_id yet).
  const filteredRecords = allRecords;

  // KPIs
  const kpi = useMemo(() => {
    const total = filteredRecords.length;
    const transported = filteredRecords.filter((r) => r.being_transported === true).length;
    // RMA = any RMA signature status recorded that isn't blank/na
    const rma = filteredRecords.filter((r) => (r.rma_signature_status || "").trim().length > 0).length;
    const inTriage = filteredRecords.filter((r) => (r.status || "").toLowerCase() === "draft").length;
    const closedForms = filteredRecords.filter((r) => (r.status || "").toLowerCase() === "submitted").length;
    const centers = new Set(filteredRecords.map((r) => r.triage_center).filter(Boolean)).size;
    const transportPct = total > 0 ? Math.round((transported / total) * 100) : 0;
    // distribution counts
    const dist = { green: 0, yellow: 0, orange: 0, red: 0, black: 0 };
    filteredRecords.forEach((r) => {
      const k = (r.triage_tag_color || "").toLowerCase();
      if (dist[k] !== undefined) dist[k]++;
    });
    return { total, transported, rma, inTriage, closedForms, centers, transportPct, dist };
  }, [filteredRecords]);

  // Open (draft) records for the patient list section
  const openForms = useMemo(
    () => filteredRecords.filter((r) => (r.status || "").toLowerCase() === "draft"),
    [filteredRecords]
  );

  // --- guards ---------------------------------------------------------------
  if (!authorized) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-6">
        <div className="text-center">
          <Warning size={40} weight="fill" className="mx-auto text-amber-400 mb-2" />
          <div className="font-display text-xl font-black text-white">Supervisors Only</div>
          <p className="text-slate-400 text-sm mt-1">The Command Post is restricted to Supervisor and above.</p>
          <Button className="mt-4 rounded-sm" onClick={() => nav("/triage")} data-testid="cp-back">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`min-h-screen bg-[#0A0F1C] text-slate-100 ${tvMode ? "tv-mode" : ""}`} data-testid="triage-command-post">
      {/* Immediate-patient alert banner — flashes when a new red-tagged
          patient is created. Auto-dismisses after 15s; click to dismiss now. */}
      {immediateAlert && (
        <button
          type="button"
          onClick={() => {
            setImmediateAlert(null);
            if (immediateAlert.id) nav(`/triage/patient/${immediateAlert.id}/edit`);
          }}
          className="fixed inset-x-0 top-0 z-[60] w-full bg-red-600 text-white px-4 py-3 shadow-lg border-b-4 border-red-800 cp-flash cursor-pointer text-left"
          data-testid="cp-immediate-alert"
        >
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 shrink-0">
              <Warning size={22} weight="fill" className="text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.25em] opacity-80">IMMEDIATE PATIENT · RED TAG</div>
              <div className="font-display font-black text-xl truncate">
                {immediateAlert.first_name || "New"} {immediateAlert.last_name || "Patient"}
                {immediateAlert.reason_for_treatment ? ` · ${immediateAlert.reason_for_treatment}` : ""}
              </div>
              <div className="text-[11px] opacity-90 truncate">
                {immediateAlert.triage_center || "No center"}{immediateAlert.event_name ? ` · ${immediateAlert.event_name}` : ""} — tap to open record
              </div>
            </div>
            <span className="text-[10px] font-bold opacity-75 shrink-0">Dismiss ✕</span>
          </div>
        </button>
      )}
      {/* Top status bar — hidden in TV mode for a clean projector view */}
      {!tvMode && (
      <header className="border-b border-slate-800/70 bg-[#0A0F1C] px-4 sm:px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/triage" className="flex items-center gap-2 text-slate-200 hover:text-white text-sm font-medium" data-testid="cp-nav-back">
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </Link>
          <div className="h-4 w-px bg-slate-700" />
          <img src={LOGO_STANDBY} alt="BSVAC" className="h-6 w-6 object-contain opacity-80" />
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className={`flex items-center gap-1.5 ${wsConnected ? "text-emerald-300" : (refreshing ? "text-sky-300" : "text-slate-400")}`}>
            {wsConnected ? <BroadcastIcon size={14} weight="bold" /> : <WifiHigh size={14} weight="bold" />}
            <span>{wsConnected ? "Real-Time" : (refreshing ? "Polling" : "Poll Only")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${paused ? "bg-slate-500" : "bg-emerald-400 animate-pulse"}`} />
            <span className={`${paused ? "text-slate-400" : "text-emerald-300"} tracking-widest font-bold`}>
              {paused ? "PAUSED" : "LIVE"}
            </span>
          </div>
          <button
            onClick={() => setPaused((p) => !p)}
            className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
            data-testid="cp-toggle-poll"
          >
            {paused ? <Play size={12} weight="bold" /> : <Pause size={12} weight="bold" />}
            <span className="font-bold">{paused ? "Resume" : "Pause"}</span>
          </button>
          <button
            onClick={() => { setPaused(false); if (loadRef.current) loadRef.current(); }}
            className="text-slate-300 hover:text-white"
            title="Refresh now"
            data-testid="cp-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
          </button>
          {canExportCsv && (
            <button
              onClick={downloadCsv}
              disabled={csvBusy}
              className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors disabled:opacity-50"
              title={selectedEventId ? "Export this event as CSV" : "Export last 90 days as CSV"}
              data-testid="cp-csv"
            >
              <FileCsv size={14} weight="bold" />
              <span className="font-bold">{csvBusy ? "Preparing…" : "CSV"}</span>
            </button>
          )}
          <button
            onClick={toggleTv}
            className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
            title={tvMode ? "Exit TV Mode" : "Enter TV Mode (projector-friendly)"}
            data-testid="cp-tv"
          >
            {tvMode ? <CornersIn size={14} weight="bold" /> : <CornersOut size={14} weight="bold" />}
            <span className="font-bold">TV</span>
          </button>
          <div className="h-4 w-px bg-slate-700" />
          <button
            onClick={async () => { await logout(); nav("/triage/login"); }}
            className="text-slate-400 hover:text-white flex items-center gap-1"
            data-testid="cp-signout"
          >
            <SignOut size={12} weight="bold" /> Sign Out
          </button>
        </div>
      </header>
      )}
      {tvMode && (
        <button
          onClick={toggleTv}
          className="fixed top-4 right-4 z-50 bg-slate-800/70 hover:bg-slate-700 text-slate-100 text-xs px-3 py-1.5 rounded-sm flex items-center gap-1"
          data-testid="cp-tv-exit"
        >
          <CornersIn size={12} weight="bold" /> Exit TV Mode
        </button>
      )}

      <main className={`mx-auto ${tvMode ? "max-w-none px-8 py-6" : "max-w-7xl px-4 sm:px-6 py-8"}`}>
        {/* Title + event dropdown */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Lightning size={tvMode ? 40 : 24} weight="fill" className="text-amber-400" />
              <h1 className={`font-display font-black tracking-tight text-white ${tvMode ? "text-6xl" : "text-3xl md:text-4xl"}`}>
                Event Command Post
              </h1>
            </div>
            <div className={`mt-2 text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 ${tvMode ? "text-sm" : "text-[11px]"}`}>
              <span data-testid="cp-last-updated">Last updated: {data.generated_at ? fmtClock(data.generated_at) : "—"}</span>
              <span className={wsConnected ? "text-emerald-400" : "text-amber-400"}>| {wsConnected ? "Live push connected" : "Polling only"}</span>
              <span className="text-slate-500">| {(() => {
                const ev = eventOptions.find((e) => e.id === selectedEventId);
                return ev ? `Filtered · ${ev.name}` : "All events (last 24h)";
              })()}</span>
            </div>
          </div>
          {!tvMode && (
            <div>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded-sm px-3 py-2 min-w-[280px] focus:outline-none focus:border-slate-500"
                data-testid="cp-event-picker"
              >
                <option value="">All events…</option>
                {eventOptions.map((ev) => (<option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}</option>))}
              </select>
            </div>
          )}
        </div>

        {err && (
          <div className="bg-red-950/70 border border-red-800 rounded-sm px-3 py-2 text-sm text-red-300 mb-4" data-testid="cp-error">
            {err}
          </div>
        )}
        {loading && !err && (
          <div className="text-center text-slate-500 text-sm py-4">Loading command post…</div>
        )}

        {/* --- KPI Row -------------------------------------------------- */}
        <div className={`grid grid-cols-2 md:grid-cols-4 mb-6 ${tvMode ? "gap-6" : "gap-3"}`}>
          <KpiCard label="Total Patients" value={kpi.total} valueClass="text-white" testid="cp-kpi-total" tvMode={tvMode} />
          <KpiCard label="Transported" value={kpi.transported} valueClass="text-sky-400" testid="cp-kpi-transported"
                   sub={`${kpi.transportPct}% rate`} tvMode={tvMode} />
          <KpiCard label="Treat & Release" value={kpi.rma} valueClass="text-orange-400" testid="cp-kpi-rma" tvMode={tvMode} />
          <KpiCard label="In Triage" value={kpi.inTriage} valueClass="text-white" testid="cp-kpi-in-triage" tvMode={tvMode} />
        </div>

        {/* --- Triage Distribution -------------------------------------- */}
        <div className={`bg-[#0F1729] border border-slate-800 rounded-sm mb-6 ${tvMode ? "p-8" : "p-5"}`} data-testid="cp-distribution">
          <div className={`font-bold tracking-[0.15em] text-slate-400 mb-3 ${tvMode ? "text-sm" : "text-[11px]"}`}>TRIAGE DISTRIBUTION</div>
          <div className={`grid grid-cols-2 sm:grid-cols-5 ${tvMode ? "gap-4" : "gap-2"}`}>
            {TAG_ORDER.map((k) => {
              const meta = TAG_META[k];
              const count = kpi.dist[k];
              return (
                <div key={k} className={`${meta.bg} rounded-sm text-center relative overflow-hidden ${tvMode ? "px-6 py-8" : "px-4 py-4"}`}
                     data-testid={`cp-bucket-${k}`}>
                  <div className={`font-display font-black leading-none ${k === "yellow" ? "text-slate-900" : "text-white"} tabular-nums ${tvMode ? "text-8xl" : "text-4xl"}`}
                       data-testid={`cp-count-${k}`}>
                    {count}
                  </div>
                  <div className={`mt-1 font-bold ${k === "yellow" ? "text-slate-900" : "text-white"} ${tvMode ? "text-2xl mt-3" : "text-[13px]"}`}>{meta.label}</div>
                  <div className={`${k === "yellow" ? "text-slate-800/80" : "text-white/80"} ${tvMode ? "text-base" : "text-[10px]"}`}>{meta.sub}</div>
                </div>
              );
            })}
          </div>
          {/* stacked % bar */}
          <DistributionBar dist={kpi.dist} total={kpi.total} tvMode={tvMode} />
        </div>

        {/* --- Secondary KPIs ------------------------------------------- */}
        <div className={`grid grid-cols-1 md:grid-cols-3 mb-6 ${tvMode ? "gap-6" : "gap-3"}`}>
          <SecondaryCard label="Open Forms (On Board)" value={kpi.inTriage} valueClass="text-amber-400" testid="cp-open-forms" tvMode={tvMode} />
          <SecondaryCard label="Closed Forms" value={kpi.closedForms} valueClass="text-emerald-400" testid="cp-closed-forms" tvMode={tvMode} />
          <SecondaryCard label="Triage Centers" value={kpi.centers} valueClass="text-white" testid="cp-centers" tvMode={tvMode} />
        </div>

        {/* --- Open Forms List / Empty state ---------------------------- */}
        <div className={`bg-[#0F1729] border border-slate-800 rounded-sm ${tvMode ? "p-8" : "p-5"}`} data-testid="cp-open-list">
          <div className={`font-bold tracking-[0.15em] text-slate-400 mb-3 flex items-center justify-between ${tvMode ? "text-sm" : "text-[11px]"}`}>
            <span>OPEN PATIENTS ON BOARD</span>
            <span className="text-slate-500">{openForms.length} open · showing open forms only</span>
          </div>
          {openForms.length === 0 ? (
            <div className={`text-center text-slate-500 ${tvMode ? "text-2xl py-24" : "text-sm py-14"}`}>
              All patient forms are closed for this event.
            </div>
          ) : (
            <div className={`grid gap-2 ${tvMode ? "md:grid-cols-3 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3"}`}>
              {openForms.map((r) => {
                const tag = TAG_META[(r.triage_tag_color || "").toLowerCase()];
                return (
                  <button key={r.id} onClick={() => nav(`/triage/patient/${r.id}/edit`)}
                          className={`text-left bg-slate-950/60 hover:bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors rounded-sm ${tvMode ? "p-4" : "p-3"}`}
                          data-testid={`cp-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className={`font-semibold text-white truncate ${tvMode ? "text-lg" : "text-sm"}`}>
                          {r.first_name} {r.last_name}
                        </div>
                        <div className={`text-slate-400 truncate ${tvMode ? "text-sm" : "text-[11px]"}`}>
                          {r.reason_for_treatment || "No reason recorded"} {r.event_name ? `· ${r.event_name}` : ""}
                        </div>
                        <div className={`text-slate-500 mono mt-0.5 ${tvMode ? "text-xs" : "text-[10px]"}`}>
                          {r.triage_center || "No center"} · Contact {r.patient_contact_time || "—"}
                        </div>
                      </div>
                      {tag && (
                        <span className={`inline-block font-bold uppercase rounded-sm ${tag.bg} ${((r.triage_tag_color || "").toLowerCase() === "yellow") ? "text-slate-900" : "text-white"} ${tvMode ? "text-xs px-3 py-1" : "text-[10px] px-2 py-0.5"}`}>
                          {tag.label}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------- helpers ---------------------------------------------------------

function KpiCard({ label, value, valueClass, testid, sub, tvMode }) {
  return (
    <div className={`bg-[#0F1729] border border-slate-800 rounded-sm ${tvMode ? "px-6 py-6" : "px-4 py-4"}`} data-testid={testid}>
      <div className={`font-bold tracking-[0.15em] text-slate-400 ${tvMode ? "text-sm" : "text-[11px]"}`}>{label.toUpperCase()}</div>
      <div className={`font-display font-black mt-2 tabular-nums ${valueClass} ${tvMode ? "text-8xl" : "text-4xl md:text-5xl"}`}>{value}</div>
      {sub && <div className={`text-slate-500 mt-1 ${tvMode ? "text-lg" : "text-[11px]"}`}>{sub}</div>}
    </div>
  );
}

function SecondaryCard({ label, value, valueClass, testid, tvMode }) {
  return (
    <div className={`bg-[#0F1729] border border-slate-800 rounded-sm text-center ${tvMode ? "px-6 py-6" : "px-4 py-4"}`} data-testid={testid}>
      <div className={`font-bold tracking-[0.15em] text-slate-400 ${tvMode ? "text-sm" : "text-[11px]"}`}>{label.toUpperCase()}</div>
      <div className={`font-display font-black mt-1 tabular-nums ${valueClass} ${tvMode ? "text-7xl" : "text-4xl md:text-5xl"}`}>{value}</div>
    </div>
  );
}

function DistributionBar({ dist, total, tvMode }) {
  if (!total) {
    return <div className={`mt-3 bg-slate-800 rounded-sm ${tvMode ? "h-4" : "h-2"}`} data-testid="cp-dist-bar-empty" />;
  }
  const seg = (k) => ({ key: k, w: (dist[k] / total) * 100, color: TAG_META[k].bar });
  const segments = TAG_ORDER.map(seg).filter((s) => s.w > 0);
  return (
    <div className="mt-3" data-testid="cp-dist-bar">
      <div className={`w-full bg-slate-800 rounded-sm overflow-hidden flex ${tvMode ? "h-5" : "h-3"}`}>
        {segments.map((s) => (
          <div key={s.key} title={`${TAG_META[s.key].label}: ${dist[s.key]}`} className={`flex items-center justify-center font-bold ${tvMode ? "text-sm" : "text-[10px]"}`}
               style={{ width: `${s.w}%`, backgroundColor: s.color, color: s.key === "yellow" ? "#0F172A" : "white" }}>
            {s.w >= 8 ? `${Math.round(s.w)}%` : ""}
          </div>
        ))}
      </div>
    </div>
  );
}
